import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Stethoscope,
  FileHeart,
  CalendarCheck,
  Pill,
  BrainCircuit,
  ShieldCheck,
  ArrowUpRight,
} from "lucide-react";

const FEATURES = [
  {
    icon: Stethoscope,
    title: "Tele Medicine",
    tag: "01 · VIRTUAL CONSULTATION",
    body: "Telemedicine leverages technology to connect patients with healthcare providers for consultations, diagnosis, and treatment, regardless of geographic barriers. It enables high-definition video calls for face-to-face interaction with healthcare professionals.",
  },
  {
    icon: FileHeart,
    title: "Electronic Health Records",
    tag: "02 · EHR",
    body: "A comprehensive digital record system for storing and managing patient health information. All patient information is securely stored and accessible in one place, including medical history, lab results, and treatment plans. Access records anytime, anywhere for efficient and seamless healthcare management.",
  },
  {
    icon: CalendarCheck,
    title: "Scheduling",
    tag: "03 · BOOKING",
    body: "A user-friendly platform that enables patients to schedule, reschedule, or cancel appointments with their healthcare providers. Features a simple and intuitive interface for booking appointments, with real-time visibility of available time slots for a seamless scheduling experience.",
  },
  {
    icon: Pill,
    title: "e-Prescription",
    tag: "04 · RX",
    body: "e-Prescription streamlines the prescribing process by enabling healthcare providers to send prescriptions electronically directly to pharmacies. Ensures quick and secure transmission of prescriptions to the patient's preferred pharmacy, reducing errors and improving convenience for both patients and healthcare professionals.",
  },
  {
    icon: BrainCircuit,
    title: "Smart Triage",
    tag: "05 · TRIAGE",
    body: "Clinical-grade assessment intelligently analyzes patient symptoms and medical history to ensure the most appropriate specialist is assigned before the consultation begins. This enhances diagnostic accuracy, streamlines patient care, and improves the overall healthcare experience.",
  },
  {
    icon: ShieldCheck,
    title: "HIPAA Compliant",
    tag: "06 · TRUST",
    body: "Patient data is protected with advanced encryption, secure access controls, and comprehensive activity tracking to ensure privacy, security, and regulatory compliance. This safeguards sensitive health information while maintaining transparency and accountability across the healthcare system.",
  },
];

const LIGHT = {
  sectionBg: "#f8fafb",
  cardBg: "#ffffff",
  cardBorder: "#e2e8f0",
  hoverBorder: "#12c4b4",
  hoverShadow: "0 12px 40px rgba(18,196,180,0.13)",
  clickBorder: "#0ba88a",
  clickShadow:
    "0 0 0 1.5px rgba(18,196,180,0.30), 0 16px 48px rgba(18,196,180,0.20)",
  glowBg: "radial-gradient(circle, rgba(18,196,180,0.14), transparent 70%)",
  tagColor: "#94a3b8",
  arrowColor: "#cbd5e1",
  iconGradient: "linear-gradient(135deg, #12c4b4 0%, #0ba88a 100%)",
  iconShadow: "0 4px 16px rgba(18,196,180,0.32)",
  titleColor: "#0a1628",
  bodyColor: "#64748b",
  footerLine: "linear-gradient(90deg, #e2e8f0 80%, transparent)",
  footerLabel: "#0a1628",
  footerIndex: "#94a3b8",
  badgeBorder: "rgba(18,196,180,0.4)",
  badgeBg: "rgba(18,196,180,0.07)",
  badgeColor: "#0ba88a",
  headingColor: "#0a1628",
  subtitleColor: "#64748b",
};

const DARK = {
  sectionBg: "#0a0800",
  cardBg: "#110d02",
  cardBorder: "rgba(200,150,30,0.30)",
  hoverBorder: "#f0c040",
  hoverShadow: "0 12px 40px rgba(200,150,30,0.18)",
  clickBorder: "#c8961e",
  clickShadow:
    "0 0 0 1.5px rgba(200,150,30,0.30), 0 16px 48px rgba(200,150,30,0.20)",
  glowBg: "radial-gradient(circle, rgba(200,150,30,0.12), transparent 70%)",
  tagColor: "#b89a4a",
  arrowColor: "#7a5c10",
  iconGradient: "linear-gradient(135deg, #c8961e 0%, #7a5c10 100%)",
  iconShadow: "0 4px 16px rgba(200,150,30,0.28)",
  titleColor: "#f5e6b0",
  bodyColor: "#b89a4a",
  footerLine: "linear-gradient(90deg, rgba(200,150,30,0.25) 80%, transparent)",
  footerLabel: "#f0c040",
  footerIndex: "#7a5c10",
  badgeBorder: "rgba(200,150,30,0.4)",
  badgeBg: "rgba(200,150,30,0.10)",
  badgeColor: "#c8961e",
  headingColor: "#f5e6b0",
  subtitleColor: "#b89a4a",
};

function useDarkMode() {
  const [isDark, setIsDark] = useState(
    () => document.documentElement.getAttribute("data-theme") === "dark",
  );
  useEffect(() => {
    const observer = new MutationObserver(() =>
      setIsDark(document.documentElement.getAttribute("data-theme") === "dark"),
    );
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    return () => observer.disconnect();
  }, []);
  return isDark;
}

function FeatureCard({ feature, index, t }) {
  const Icon = feature.icon;

  function handleMouseEnter(e) {
    const el = e.currentTarget;
    el.style.borderTopColor = t.cardBorder;
    el.style.borderRightColor = t.cardBorder;
    el.style.borderBottomColor = t.cardBorder;
    el.style.borderLeftColor = t.hoverBorder;
    el.style.borderLeftWidth = "3px";
    el.style.boxShadow = t.hoverShadow;
    el.style.transform = "translateY(-4px)";
  }
  function handleMouseLeave(e) {
    const el = e.currentTarget;
    el.style.borderTopColor = t.cardBorder;
    el.style.borderRightColor = t.cardBorder;
    el.style.borderBottomColor = t.cardBorder;
    el.style.borderLeftColor = t.cardBorder;
    el.style.borderLeftWidth = "1px";
    el.style.boxShadow = "none";
    el.style.transform = "translateY(0)";
  }
  function handleClick(e) {
    const el = e.currentTarget;
    el.style.borderLeftColor = t.clickBorder;
    el.style.borderLeftWidth = "4px";
    el.style.boxShadow = t.clickShadow;
  }

  return (
    <motion.article
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.5, delay: index * 0.06 }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
      style={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        borderRadius: "20px",
        borderTop: `1px solid ${t.cardBorder}`,
        borderRight: `1px solid ${t.cardBorder}`,
        borderBottom: `1px solid ${t.cardBorder}`,
        borderLeft: `1px solid ${t.cardBorder}`,
        background: t.cardBg,
        padding: "26px 28px",
        cursor: "pointer",
        transition:
          "transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease, background 0.3s",
      }}
    >
      <div
        style={{
          pointerEvents: "none",
          position: "absolute",
          right: "-50px",
          top: "-50px",
          width: "180px",
          height: "180px",
          borderRadius: "50%",
          background: t.glowBg,
          filter: "blur(28px)",
        }}
      />

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          fontSize: "10px",
          fontWeight: 700,
          letterSpacing: "0.16em",
          color: t.tagColor,
          textTransform: "uppercase",
          fontFamily: "'Plus Jakarta Sans',system-ui,sans-serif",
        }}
      >
        <span>{feature.tag}</span>
        <ArrowUpRight size={14} style={{ color: t.arrowColor }} />
      </div>

      <div
        style={{
          marginTop: "24px",
          width: "54px",
          height: "54px",
          borderRadius: "50%",
          background: t.iconGradient,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: t.iconShadow,
          flexShrink: 0,
        }}
      >
        <Icon size={22} color="#ffffff" strokeWidth={1.8} />
      </div>

      <h3
        style={{
          marginTop: "18px",
          fontFamily: "'Sora',system-ui,sans-serif",
          fontSize: "1.3rem",
          fontWeight: 700,
          letterSpacing: "-0.5px",
          color: t.titleColor,
          lineHeight: 1.2,
        }}
      >
        {feature.title}
      </h3>

      <p
        style={{
          marginTop: "8px",
          fontSize: "13.5px",
          lineHeight: 1.7,
          color: t.bodyColor,
          fontFamily: "'Plus Jakarta Sans',system-ui,sans-serif",
        }}
      >
        {feature.body}
      </p>

      <div style={{ marginTop: "auto", paddingTop: "22px" }}>
        <div style={{ height: "1px", background: t.footerLine }} />
        <div
          style={{
            marginTop: "14px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <span
            style={{
              fontFamily: "monospace",
              fontSize: "11px",
              color: t.footerIndex,
            }}
          >
            /0{index + 1}
          </span>
        </div>
      </div>
    </motion.article>
  );
}

export function Features() {
  const isDark = useDarkMode();
  const t = isDark ? DARK : LIGHT;

  return (
    <section
      id="features"
      style={{
        background: t.sectionBg,
        padding: "6rem 0",
        transition: "background 0.3s",
      }}
    >
      <div
        style={{ maxWidth: "1200px", margin: "0 auto", padding: "0 1.5rem" }}
      >
        {/* ── Section header ── */}
        <div className="features-header">
          <div style={{ maxWidth: "580px" }}>
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                borderRadius: "9999px",
                border: `1px solid ${t.badgeBorder}`,
                background: t.badgeBg,
                padding: "4px 14px",
                fontSize: "10px",
                fontWeight: 700,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                color: t.badgeColor,
                marginBottom: "18px",
                fontFamily: "'Plus Jakarta Sans',system-ui,sans-serif",
              }}
            >
              ◆ Platform
            </span>
            <h2
              style={{
                fontFamily: "'Sora',system-ui,sans-serif",
                fontSize: "clamp(1.75rem,3.5vw,3rem)",
                fontWeight: 500,
                letterSpacing: "-1.5px",
                color: t.headingColor,
                lineHeight: 1.1,
              }}
            >
              One Platform, Complete
              <br />
              Healthcare.
            </h2>
          </div>
          <p
            className="features-subtitle"
            style={{
              fontSize: "14px",
              lineHeight: 1.7,
              color: t.subtitleColor,
              fontFamily: "'Plus Jakarta Sans',system-ui,sans-serif",
            }}
          >
            Tools trusted — built to deliver high-quality care from anywhere.
          </p>
        </div>

        {/* ── Card grids ──
            Desktop: row1=2col, row2=3col, row3=1col (last card)
            Mobile:  all single column via CSS class override
        ── */}
        <div className="grid sm:grid-cols-1 grid-cols-2 gap-5">
          {FEATURES.map((f, i) => (
            <FeatureCard key={f.title} feature={f} index={i} t={t} />
          ))}
        </div>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800&family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap');

        /* ── Desktop (default) ── */
        .features-header {
          display: flex;
          flex-wrap: wrap;
          align-items: flex-end;
          justify-content: space-between;
          gap: 1.5rem;
          margin-bottom: 3.5rem;
        }
        .features-subtitle {
          max-width: 300px;
          text-align: right;
        }
        .features-grid {
          gap: 18px;
          margin-bottom: 18px;
          display: grid;
        }
        .features-grid--2 { grid-template-columns: repeat(2, 1fr); }
        .features-grid--3 { grid-template-columns: repeat(3, 1fr); }

        /* ── Mobile ── */
        @media (max-width: 767px) {
          /* Header: stack, left-align subtitle */
          .features-header {
            flex-direction: column;
            align-items: flex-start;
            margin-bottom: 2rem;
          }
          .features-subtitle {
            max-width: 100%;
            text-align: left;
          }

          /* All grids → single column */
          .features-grid--2,
          .features-grid--3 {
            grid-template-columns: 1fr;
          }

          /* Section padding reduce */
          #features {
            padding: 4rem 0 !important;
          }
        }

        /* ── Tablet (768–1023px): 2 col always ── */
        @media (min-width: 768px) and (max-width: 1023px) {
          .features-grid--3 { grid-template-columns: repeat(2, 1fr); }
        }
      `}</style>
    </section>
  );
}

export default Features;
